import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from '@remix-run/react';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import './app.css';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { getEnv } from './server/env.server';
import { useNonce } from './utils/nonce-provider.ts';
import { authSessionStorage } from './server/sessions.ts';
import { useToast } from './components/use-toast.ts';
import { useEffect } from 'react';

export const links: LinksFunction = () => [
  {
    rel: 'stylesheet',
    href: 'https://cloud.typography.com/6959554/6550192/css/fonts.css',
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await authSessionStorage.getSession(
    request.headers.get('Cookie')
  );
  const message = session.get('message') || null;
  session.flash('message', null);

  return json(
    {
      message,
      ENV: getEnv(),
    },
    {
      headers: {
        'Set-Cookie': await authSessionStorage.commitSession(session),
      },
    }
  );
}

export function Document({
  children,
  nonce,
  env = {},
  allowIndexing = true,
}: {
  children: React.ReactNode;
  nonce: string;
  env?: Record<string, string>;
  allowIndexing?: boolean;
}) {
  return (
    <html lang="en">
      <head>
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <Links />
      </head>
      <body className="bg-surface-base text-default dark">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)};`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false';
  const { toast } = useToast();
  useEffect(() => {
    if (data.message) {
      toast(data.message);
    }
  }, [data.message, toast]);

  return (
    <Document nonce={nonce} env={data.ENV} allowIndexing={allowIndexing}>
      <Outlet />
    </Document>
  );
}

export default withSentry(App);

export const ErrorBoundary = () => {
  const nonce = useNonce();
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
};
